import { useState, useEffect } from 'react';
import { Form, Input, Button, message, Card, Upload, Collapse, Image, Divider, Space } from 'antd';
import { UploadOutlined, GlobalOutlined, FileImageOutlined, PictureOutlined, SearchOutlined } from '@ant-design/icons';
import API from '../api/client';
import { BACKEND_URL } from '../../config';

const { TextArea } = Input;

const SettingsPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [faviconUrl, setFaviconUrl] = useState('');
  const [headerLogoUrl, setHeaderLogoUrl] = useState('');
  const [footerLogoUrl, setFooterLogoUrl] = useState('');
  const [ogImageUrl, setOgImageUrl] = useState('');

  useEffect(() => {
    API.get('/settings').then((res) => {
      if (res.data && res.data._id) {
        form.setFieldsValue(res.data);
        setFaviconUrl(res.data.favicon || '');
        setHeaderLogoUrl(res.data.headerLogo || '');
        setFooterLogoUrl(res.data.footerLogo || '');
        setOgImageUrl(res.data.ogImage || '');
      }
    });
  }, [form]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      values.favicon = faviconUrl;
      values.headerLogo = headerLogoUrl;
      values.footerLogo = footerLogoUrl;
      values.ogImage = ogImageUrl;
      await API.put('/settings', values);
      message.success('Ayarlar kaydedildi');
    } catch (err) {
      if (err?.errorFields) {
        message.warning('Lütfen zorunlu alanları doldurun');
      } else {
        message.error('Kaydetme hatası oluştu');
      }
    }
    setLoading(false);
  };

  const handleUpload = (setter, fieldName) => async (info) => {
    const formData = new FormData();
    formData.append('file', info.file);
    try {
      const res = await API.post('/upload', formData);
      const url = res.data.url;
      setter(url);
      form.setFieldValue(fieldName, url);
      message.success('Dosya yüklendi');
    } catch {
      message.error('Yükleme hatası');
    }
  };

  const ImagePreview = ({ url, label }) => (
    url ? (
      <div style={{ marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>{label}</span>
        <Image src={`${BACKEND_URL}${url}`} width={120} style={{ borderRadius: 8, border: '1px solid #eee' }} />
      </div>
    ) : null
  );

  const collapseItems = [
    {
      key: 'seo',
      label: <span><SearchOutlined style={{ marginRight: 8 }} />SEO & Meta Etiketleri</span>,
      children: (
        <>
          <Form.Item name="siteTitle" label="Site Başlığı (Title)" rules={[{ required: true, message: 'Site başlığı zorunlu' }]}>
            <Input placeholder="Dihiz - Didim Belediyesi İştiraki" maxLength={70} showCount />
          </Form.Item>
          <Form.Item name="metaDescription" label="Meta Açıklama (Description)">
            <TextArea rows={3} placeholder="Dihiz Turizm Organizasyon & Restoran, Didim Belediyesi bünyesinde..." maxLength={160} showCount />
          </Form.Item>
          <Form.Item name="metaKeywords" label="Meta Anahtar Kelimeler (Keywords)">
            <TextArea rows={2} placeholder="dihiz, didim, belediye, restoran, turizm, organizasyon" />
          </Form.Item>
          <Divider>Open Graph (Sosyal Medya Paylaşım)</Divider>
          <Form.Item name="ogTitle" label="OG Başlık">
            <Input placeholder="Sosyal medyada görünecek başlık (boş ise site başlığı kullanılır)" />
          </Form.Item>
          <Form.Item name="ogDescription" label="OG Açıklama">
            <TextArea rows={2} placeholder="Sosyal medyada görünecek açıklama" maxLength={200} showCount />
          </Form.Item>
          <Form.Item label="OG Görsel (Paylaşım Görseli)">
            <Space direction="vertical" style={{ width: '100%' }}>
              <ImagePreview url={ogImageUrl} label="Mevcut OG Görsel" />
              <Upload customRequest={handleUpload(setOgImageUrl, 'ogImage')} showUploadList={false} accept="image/*">
                <Button icon={<UploadOutlined />}>OG Görsel Yükle</Button>
              </Upload>
              <span style={{ fontSize: 12, color: '#888' }}>Önerilen boyut: 1200x630 piksel</span>
            </Space>
          </Form.Item>
          <Divider>Twitter Card</Divider>
          <Form.Item name="twitterCard" label="Twitter Card Tipi">
            <Input placeholder="summary_large_image" />
          </Form.Item>
          <Form.Item name="twitterSite" label="Twitter Hesabı">
            <Input placeholder="@dihiz" />
          </Form.Item>
          <Divider>Diğer</Divider>
          <Form.Item name="canonicalUrl" label="Canonical URL">
            <Input placeholder="https://www.dihiz.com" />
          </Form.Item>
          <Form.Item name="robots" label="Robots Meta">
            <Input placeholder="index, follow" />
          </Form.Item>
          <Form.Item name="googleVerification" label="Google Site Doğrulama Kodu">
            <Input placeholder="Google Search Console doğrulama kodu" />
          </Form.Item>
        </>
      ),
    },
    {
      key: 'favicon',
      label: <span><GlobalOutlined style={{ marginRight: 8 }} />Favicon</span>,
      children: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <ImagePreview url={faviconUrl} label="Mevcut Favicon" />
          <Upload customRequest={handleUpload(setFaviconUrl, 'favicon')} showUploadList={false} accept="image/*,.ico">
            <Button icon={<UploadOutlined />}>Favicon Yükle</Button>
          </Upload>
          <span style={{ fontSize: 12, color: '#888' }}>Önerilen: 32x32 veya 64x64 piksel, .ico / .png formatı</span>
        </Space>
      ),
    },
    {
      key: 'headerLogo',
      label: <span><PictureOutlined style={{ marginRight: 8 }} />Header Logosu</span>,
      children: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <ImagePreview url={headerLogoUrl} label="Mevcut Header Logosu" />
          <Upload customRequest={handleUpload(setHeaderLogoUrl, 'headerLogo')} showUploadList={false} accept="image/*">
            <Button icon={<UploadOutlined />}>Header Logo Yükle</Button>
          </Upload>
          <span style={{ fontSize: 12, color: '#888' }}>Önerilen: PNG formatında, şeffaf arkaplan, 200x200 piksel</span>
        </Space>
      ),
    },
    {
      key: 'footerLogo',
      label: <span><FileImageOutlined style={{ marginRight: 8 }} />Footer Logosu</span>,
      children: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <ImagePreview url={footerLogoUrl} label="Mevcut Footer Logosu" />
          <Upload customRequest={handleUpload(setFooterLogoUrl, 'footerLogo')} showUploadList={false} accept="image/*">
            <Button icon={<UploadOutlined />}>Footer Logo Yükle</Button>
          </Upload>
          <span style={{ fontSize: 12, color: '#888' }}>Önerilen: PNG formatında, şeffaf arkaplan</span>
        </Space>
      ),
    },
  ];

  return (
    <>
      <h2>Site Ayarları</h2>
      <Card>
        <Form form={form} layout="vertical" style={{ maxWidth: 700 }}>
          <Collapse items={collapseItems} defaultActiveKey={['seo']} style={{ marginBottom: 24 }} />
          <Button type="primary" onClick={handleSave} loading={loading} size="large" block>
            Tüm Ayarları Kaydet
          </Button>
        </Form>
      </Card>
    </>
  );
};

export default SettingsPage;
